import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SelectedItemRenderProps } from '@openx/components-core/lib/SelectionDrawer/types';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle';
import { SelectedContextualSegmentItem, type SelectedSegmentWithMode } from './SelectedContextualSegmentItem';

type SectionHeaderItem = {
  type: 'section-header';
  sectionType: 'includes' | 'excludes';
  id: string;
};

type EmptyStateItem = {
  type: 'empty-state';
  sectionType: 'includes' | 'excludes';
  id: string;
};

type SegmentItem = SelectedSegmentWithMode & {
  type: 'segment';
};

export type GroupedSegmentItem = SectionHeaderItem | EmptyStateItem | SegmentItem;

type SelectedSegmentsWithSectionsProps = SelectedItemRenderProps<GroupedSegmentItem>;

export const SelectedSegmentsWithSections = ({ item, onRemove }: SelectedSegmentsWithSectionsProps) => {
  const { t } = useTranslation();

  const handleRemove = useCallback(() => onRemove(item), [onRemove, item]);

  if (item.type === 'section-header') {
    const sectionTitle = item.sectionType === 'includes' ? t('INCLUDED') : t('EXCLUDED');

    return (
      <Box data-test={`section-header-${item.sectionType}`}>
        <FiltersTitle title={sectionTitle} readonly marginTopValue={2} color="textSecondary" sx={{ mx: 0, px: 0 }} />
      </Box>
    );
  }

  if (item.type === 'empty-state') {
    const emptyMessage =
      item.sectionType === 'includes'
        ? t('No included segments were applied.')
        : t('No excluded segments were applied.');

    return (
      <Box
        sx={{ padding: theme => theme.spacing(2, 2.5), color: 'text.secondary' }}
        data-test={`empty-state-${item.sectionType}`}
      >
        <Typography variant="body2">{emptyMessage}</Typography>
      </Box>
    );
  }

  const segmentItem: SelectedSegmentWithMode = {
    providerId: item.providerId,
    segment: item.segment,
    inclusionType: item.inclusionType,
    readonly: item.readonly,
    isMisconfigured: item.isMisconfigured,
  };

  return <SelectedContextualSegmentItem item={segmentItem} onRemove={handleRemove} />;
};
